import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { CommonButton } from 'gemlux-common-components';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { authActions } from '../../../redux/auth/authSlice';
import { AnswerType } from '../../../utils/enums/answer-type.enum';
import { CONTACT_US_ROUTE } from '../../../utils/routes';
import TickSvg from '../../../assets/img/tick-white.svg';
import LogoLink from '../../LogoLink/LogoLink';

type IQuestionProps = {
  section1: string;
  section2?: string;
  activeStep: number;
  setActiveStep: (_activeStep: number) => void;
  leftButtonText?: string;
  rightButtonText?: string;
};

function Question({ section1, section2, activeStep, setActiveStep, leftButtonText, rightButtonText }: IQuestionProps) {
  const dispatch = useAppDispatch();
  const { questionOneAnswer, questionTwoAnswer } = useAppSelector((state) => state.auth.signUpData);
  const [selectedOption, setSelectedOption] = useState<AnswerType | null>(null);

  useEffect(() => {
    if (activeStep === 0 && questionOneAnswer) {
      setSelectedOption(questionOneAnswer);
    } else if (activeStep === 1 && questionTwoAnswer) {
      setSelectedOption(questionTwoAnswer);
    }
  }, [activeStep, questionOneAnswer, questionTwoAnswer]);

  const handleSelect = (answer: AnswerType) => {
    setSelectedOption(answer);
    if (activeStep === 0) {
      if (answer === AnswerType.NO) {
        dispatch(authActions.setSignUpData({ lastActiveStep: activeStep + 1, questionOneAnswer: answer }));
        setActiveStep(activeStep + 1);
      } else {
        dispatch(authActions.setSignUpData({ lastActiveStep: activeStep + 3, questionOneAnswer: answer }));
        setActiveStep(activeStep + 3);
      }
    }
    if (activeStep === 1) {
      if (answer === AnswerType.NO) {
        dispatch(authActions.setSignUpData({ lastActiveStep: activeStep + 1, questionTwoAnswer: answer }));
        setActiveStep(activeStep + 1);
      } else {
        dispatch(authActions.setSignUpData({ lastActiveStep: activeStep + 2, questionTwoAnswer: answer }));
        setActiveStep(activeStep + 2);
      }
    }
    if (activeStep === 2) {
      if (answer === AnswerType.INQUIRE_ABOUT_KROMA_KRISTA) {
        window.open(CONTACT_US_ROUTE, '_blank');
      } else {
        dispatch(authActions.setSignUpData({ lastActiveStep: activeStep + 1 }));
        setActiveStep(activeStep + 1);
      }
    }
  };

  const isCustomButtonText = leftButtonText !== undefined || rightButtonText !== undefined;

  return (
    <Grid
      display="flex"
      gap={6}
      flexDirection="column"
      sx={{
        maxWidth: '380px',
        backgroundColor: '#FFFFFF',
        padding: '5%',
        borderRadius: '8px 8px 0px 0px',
        border: '1px solid #DCE0E5',
        borderBottom: '0',
      }}
    >
      <Box display="flex" flexDirection="column" alignItems="center" gap="10px">
        <LogoLink padding="5%" width="173.343px" height="32px" />
      </Box>
      <Grid alignItems="center" display="flex" flexDirection="column">
        <Typography textAlign="center" maxWidth={555} variant="calloutInterRegular">
          {section1}
        </Typography>
        {section2 && (
          <>
            <br />
            <Typography textAlign="center" maxWidth={445} variant="calloutInterRegular">
              {section2}
            </Typography>
          </>
        )}
      </Grid>
      <Grid
        display="flex"
        gap={isCustomButtonText ? 2 : { xs: 2, sm: 0 }}
        sx={{
          flexDirection: isCustomButtonText ? 'column' : { xs: 'column', sm: 'row' },
          alignItems: isCustomButtonText ? 'stretch' : { xs: 'center', sm: 'stretch' },
          justifyContent: { xs: 'center', sm: 'space-between' },
          paddingX: 1,
        }}
      >
        <CommonButton
          variant={selectedOption === AnswerType.NO ? 'contained' : 'outlined'}
          backgroundColor={selectedOption === AnswerType.NO ? '#3348FF' : 'transparent'}
          textColor={selectedOption === AnswerType.NO ? 'white' : '#5261FE'}
          sx={{
            border: '1px solid #3348FF',
            borderRadius: '8px',
            ...(isCustomButtonText && { width: '100%' }),
            minWidth: '150px',
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => handleSelect(activeStep === 2 ? AnswerType.INQUIRE_ABOUT_KROMA_KRISTA : AnswerType.NO)}
          startIcon={selectedOption === AnswerType.NO && <Box component="img" src={TickSvg} />}
        >
          {rightButtonText || 'No'}
        </CommonButton>
        <CommonButton
          variant={selectedOption === AnswerType.YES ? 'contained' : 'outlined'}
          backgroundColor={selectedOption === AnswerType.YES ? '#3348FF' : 'transparent'}
          textColor={selectedOption === AnswerType.YES ? 'white' : '#5261FE'}
          sx={{
            border: '1px solid #3348FF',
            borderRadius: '8px',
            ...(isCustomButtonText && { width: '100%' }),
            minWidth: '150px',
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => handleSelect(AnswerType.YES)}
          startIcon={selectedOption === AnswerType.YES && <Box component="img" src={TickSvg} />}
        >
          {leftButtonText || 'Yes'}
        </CommonButton>
      </Grid>
    </Grid>
  );
}

export default Question;
